import { Link } from 'react-router-dom';
import { optimizeImage } from '@/lib/imageOptimization';
import { useSmartImage } from '@/hooks/useSmartImage';

export interface RelatedArticle {
  id: string;
  title: string;
  slug: string;
  cover_image: string | null;
  topics?: { name: string; tag_color: string | null } | null;
  author_profile?: { name: string } | null;
}

export interface RelatedArticleCardProps {
  /** Article data */
  article: RelatedArticle;
  /** Additional className */
  className?: string;
  /** ⚠️ ANALYTICS: Callback when article is clicked */
  onClick?: () => void;
}

/**
 * component-card-article-related
 * 
 * A compact article card for the "Related" section following the atomic design token system.
 * Apple-inspired design with 4:3 image, topic label, and 2-line title.
 * 
 * Tokens applied:
 * - Image: aspect-[4/3], rounded-3xl (primitive-radius-3xl), hover:scale-105
 * - Topic: Uppercase, text-12, topic color or Coal fallback
 * - Title: text-21 font-bold text-carbon, line-clamp-2
 * - Hover: Image scale effect on desktop
 */
export function RelatedArticleCard({ article, className = '', onClick }: RelatedArticleCardProps) {
  const topicColor = article.topics?.tag_color || undefined;
  const { ref, inView } = useSmartImage();
  
  // ⚠️ ANALYTICS: Handle click with tracking callback
  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick();
    }
  };
  
  return (
    <Link
      to={`/blog/${article.slug}`}
      className={`group block ${className}`}
      onClick={handleClick}
    >
      {/* Image with hover effect */}
      <div ref={ref} className="relative aspect-[4/3] rounded-3xl overflow-hidden mb-4 bg-grey">
        {inView && article.cover_image ? (
          <img
            src={optimizeImage(article.cover_image, { width: 600 })}
            alt={article.title}
            loading="lazy"
            decoding="async"
            width={600}
            height={450}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : !article.cover_image ? (
          <div className="w-full h-full bg-grey" />
        ) : null}
      </div>

      {/* Topic label */}
      {article.topics?.name && (
        <p 
          className="text-12 font-medium uppercase tracking-wider mb-2"
          style={{ color: topicColor || 'var(--coal)' }}
        >
          {article.topics.name}
        </p>
      )}

      {/* Title - 2 lines max */}
      <h3 className="text-21 font-bold text-carbon line-clamp-2 leading-tight">
        {article.title}
      </h3>
    </Link>
  );
}

export default RelatedArticleCard;
